import { createSlice } from '@reduxjs/toolkit'
import { normalize, schema } from 'normalizr'
import { api, routes } from 'core/api'
import { cartTypes } from 'constants'
import { createFormDataFromJSON } from 'utils/misc'
import { requestGetCart } from '@state/cart/cartSlice'
import Toaster from 'features/toaster'

const initialState = {
    isFetchingWishlist: false,
    error: null,
    settings: {},
    isRemovingItem: false,

    orderTotals: {},
    entities: {
        items: {},
    },

    result: [],
}

function fetchStart(state) {
    state.error = null
    state.isFetchingWishlist = true
}

function fetchSuccess(state, action) {
    const { data, orderTotals, settings } = action.payload
    const { items } = data

    const nextEntities = {
        items: {},
    }

    const nextIds = []

    items.forEach(item => {
        nextEntities.items[item.id] = item
        nextIds.push(item.id)
    })
    
    state.settings = settings;
    state.entities = nextEntities;
    state.result = nextIds
    state.orderTotals = orderTotals
}

function fetchFailure(state, action) {
    const { error } = action.payload
    state.isFetchingWishlist = false
    state.error = error
}
// ###########################

// ####### Add item #######
function addItemSuccess(state, action) {
    const { items } = action.payload

    const nextEntities = {
        items: {},
    }

    const nextIds = []

    items.forEach(item => {
        nextEntities.items[item.id] = item
        nextIds.push(item.id)
    })

    state.entities = nextEntities;
    state.result = nextIds
    state.isFetchingCart = false;
} 

function addSciId(state, action) {
    const { productId, id } = action.payload
    state.entities.items[productId].id = id
}

function addItemFailure(state, action) {
    const { error } = action.payload

    state.error = error
}
// ###########################

// ####### Remove item #######
function removeItemStart(state) {
    state.isRemovingItem = true
    state.error = null
}

function removeItemSuccess(state, action) {
    const { productId } = action.payload

    const prevState = { ...state.entities.items }
    const prevResults = [...state.result]

    const nextResult = prevResults.filter(res => res !== productId)

    const nextState = prevState
    delete nextState[productId]

    state.entities.items = nextState
    state.result = nextResult
}

function removeItemFailure(state, action) {
    const { error } = action.payload

    state.isRemovingItem = false
    state.error = error
}
// ###########################

// ####### Update item #######
function updateItemStart(state) {
    state.error = null
}

function updateItemSuccess(state, action) {
    const { productId, quantity } = action.payload

    const prevState = { ...state.entities.items }

    const existingItem = prevState[productId]

    if (existingItem) {
        const nextState = {
            ...prevState,
            ...(prevState[existingItem.productId].quantity = quantity),
        }

        state.entities.items = nextState
    }
}

function updateItemFailure(state, action) {
    const { error } = action.payload

    state.error = error
}
// ###########################

function error(state, action) {
    const { error } = action.payload
    state.error = error
}

const wishlistSlice = createSlice({
    name: 'wishlist',
    initialState,
    reducers: {
        addItemToWishlistSuccess: addItemSuccess,
        addItemToWishlistFailure: addItemFailure,

        addSciIdToProduct: addSciId,

        removeWishlistItemStart: removeItemStart,
        removeWishlistItemSuccess: removeItemSuccess,
        removeWishlistItemFailure: removeItemFailure,

        updateWishlistItemStart: updateItemStart,
        updateWishlistItemSuccess: updateItemSuccess,
        updateWishlistItemFailure: updateItemFailure,

        fetchWishlistStart: fetchStart,
        fetchWishlistSuccess: fetchSuccess,
        fetchWishlistFailure: fetchFailure,

        genericError: error,
    },
})

export const {
    addItemToWishlistSuccess,
    addItemToWishlistFailure,

    addSciIdToProduct,

    removeWishlistItemStart,
    removeWishlistItemSuccess,
    removeWishlistItemFailure,

    updateWishlistItemStart,
    updateWishlistItemSuccess,
    updateWishlistItemFailure,

    fetchWishlistStart,
    fetchWishlistSuccess,
    fetchWishlistFailure,

    genericError,
} = wishlistSlice.actions

const wishlistSchema = [
    new schema.Entity('items', {}, { idAttribute: 'id' }),
]

/**
 * @description Updates the wishlist state completely, no parameters needed.
 */
export function requestGetWishlist() {
    return async dispatch => {
        try {
            await dispatch(fetchWishlistStart())

            const { data, settings, orderTotals } = await api.get(
                routes.wishlist.get,
            )

            const normalizedData = normalize(data.items, wishlistSchema)

            await dispatch(
                fetchWishlistSuccess({
                    orderTotals,
                    data,
                    settings,
                    normalizedData,
                }),
            )
        } catch (error) {
            dispatch(fetchWishlistFailure({ error }))
        }
    }
}

export function requestAddItemToCartFromWishlist({ body, route }) {
    return async dispatch => {
        try {
            const data = await fetch(route, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(body),
            })
                .then(res => res.json())
                .then(d => d);

            if (data.success === true) {
                await dispatch(requestGetWishlist())
                await dispatch(requestGetCart())
            }
        }
        catch (ex) {
            console.warn(ex);
        }
    }
}

export function requestAddWishlistToCart({ route, body }) {
    return async dispatch => {
        const data = await fetch(route, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(body),
        }).then(res => res.json()).then(d => d);

        if (data.success === true) {
            await dispatch(requestGetWishlist())
            await dispatch(requestGetCart())
        }
    }
}

/**
 * @param {string} sciId    Shopping Cart Id to find item in cart to delete
 * @param {*} productId     Global product Id
 */
export function requestDeleteItemFromWishlist(sciId) {
    return async dispatch => {
        try {
            dispatch(removeWishlistItemStart())

            const { success } = await api.post(routes.wishlist.remove(sciId), {
                sciId,
            })

            if (success) {
                dispatch(requestGetWishlist())
            } else {
                dispatch(removeWishlistItemFailure())
            }

        } catch (error) {
            dispatch(removeWishlistItemFailure({ error }))
        }
    }
}

export function requestUpdateWishlistItemQuantity(
    productId,
    sciId,
    quantity,
    additive = true,
) {
    return async dispatch => {
        try {
            dispatch(updateWishlistItemStart())

            const { success } = await api.post(routes.wishlist.quantity(sciId), {
                quantity,
                isAbsoluteQuantity: !additive,
            })

            if (success) {
                dispatch(requestGetWishlist())
            } else {
                dispatch(updateItemFailure())
            }
        } catch (error) {
            dispatch(updateWishlistItemFailure({ error }))
        }
    }
}

export function requestAddItemToWishlist(product, data, from, additive = true) {
    const quantityKey = `addtocart_${product.id}.EnteredQuantity`
    const quantityValue = product.quantity || 1
    let form

    if (data) {
        // In this case there was more data to add
        // as form data, i.e. product attributes,
        // that is received as a JSON string, so we need to deserialize it
        form = createFormDataFromJSON(data)
        form.set(quantityKey, quantityValue)
    }

    if (!data) {
        // No additional data, but we still need to send the quantity as a formdata key
        form = new FormData()
        form.set(quantityKey, quantityValue)
    }

    return async dispatch => {
        try {
            const url =
                from === 'details'
                    ? routes.wishlist.addDetails(
                          product.id,
                          cartTypes.Wishlist,
                          quantityValue,
                      )
                    : routes.wishlist.add(
                          product.id,
                          cartTypes.Wishlist,
                          quantityValue,
                      )

            const { success, message } =
                await api.post(
                    url,
                    form || {
                        isAbsoluteQuantity: !additive,
                    },
                    false,
                )
            
            let errors = ''
            let wishlistData = null

            Array.isArray(message)
                ? message.map(m => {
                    errors += m
                })
                : message
            
            if (success) {
                wishlistData = data;
            }

            return new Promise((resolve, reject) => {
                if (success) {
                    resolve({ message, success });
                    dispatch(requestGetWishlist())

                    // dispatch(addItemToWishlistSuccess({ items: wishlistData.items }));
                } else {
                    reject(new Error(message)); 
                    dispatch(addItemToWishlistFailure({ message: errors }));
                }
                
            })
        } catch (error) {
            dispatch(addItemToWishlistFailure({ error }))
        }
    }
}

export default wishlistSlice.reducer
